import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence, SequenceEvent } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 20 — Pivot Thirds (Minor Scale, Part 2).
 *
 * Same anchor-and-floating-thirds drill as Lesson 11, but on minor scales
 * (natural and harmonic) and with rhythmic complexity layered on top:
 * 3-3-2 accent groupings inside 4/4, then odd-meter time signatures
 * (5/8 = 3+2, 7/8 = 3+2+2), each grouped into "small numbers" that the
 * body can actually feel.
 *
 * Bespoke visual: an Accent Grid. A row of 16 cells (one per sixteenth)
 * with the accents highlighted on the picked grouping. The student SEES
 * 3-3-2-3-3-2 across the bar and recognises why "irregular" rhythms feel
 * natural once you see them broken into small chunks.
 */

interface AccentPattern {
  id: string;
  label: string;
  meter: string;
  groups: number[]; // sums to a power of two or to the meter denominator
  /** What ONE cell of `groups` is worth. A 3+3+2 bar of eighths is not the
   *  same speed as a 3+3+3+3+4 bar of sixteenths — playing both as sixteenths
   *  ran the Latin pulse and both odd meters at double time. */
  cell: "8n" | "16n";
  description: string;
}

const PATTERNS: AccentPattern[] = [
  { id: "44-3-3-2",       label: "3 · 3 · 2",        meter: "4/4 (8)",  groups: [3, 3, 2],       cell: "8n",  description: "8 eighth notes per bar grouped 3+3+2. The classic Latin-tinted pulse." },
  { id: "44-3-3-3-3-4",   label: "3 · 3 · 3 · 3 · 4",meter: "4/4 (16)", groups: [3, 3, 3, 3, 4], cell: "16n", description: "16 sixteenths grouped 3+3+3+3+4 — the grouping Jason connects with Bryan Adams (Summer of '69)." },
  { id: "58-3-2",         label: "3 · 2",            meter: "5/8",      groups: [3, 2],          cell: "8n",  description: "5/8 felt as 3+2." },
  { id: "58-2-3",         label: "2 · 3",            meter: "5/8",      groups: [2, 3],          cell: "8n",  description: "Same 5/8 with the long beat at the end. Different flavour, same maths." },
  { id: "78-3-2-2",       label: "3 · 2 · 2",        meter: "7/8",      groups: [3, 2, 2],       cell: "8n",  description: "7/8 grouped 3+2+2 — counted the way rock and progressive drummers break up odd meters." },
  { id: "78-2-2-3",       label: "2 · 2 · 3",        meter: "7/8",      groups: [2, 2, 3],       cell: "8n",  description: "Same 7/8, accent on the long beat at the end." },
];

export default function PianoIntermediate20() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "20");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider
        defaultTonic="G"
        availableQualities={["natural-minor", "harmonic-minor"]}
      >
        <PIShell
          title="Pivot Thirds — Minor Scale (Part 2)"
          exerciseGoal="Take the pivot drill to its rhythmic extreme. Anchor your pinky on Sa or Pa, float minor thirds, then pile on accent groupings and odd-meter time signatures."
          bpm={88}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [patternIdx, setPatternIdx] = useState(0);
  const pattern = PATTERNS[patternIdx];

  // Build the seven third pairs in the current minor scale
  const scaleSemis = k.quality === "natural-minor"
    ? [0, 2, 3, 5, 7, 8, 10]
    : [0, 2, 3, 5, 7, 8, 11]; // harmonic minor

  const playPattern = async () => {
    kickAudio();
    // Generic: play the tonic on every accented beat, fill with a third pair on the others.
    // Simplified for the audio demo; the rhythmic shape is what's pedagogically important.
    const BPM = 88;
    // One cell is whatever the pattern says it is — an eighth for the 3+3+2
    // and the odd meters, a sixteenth for the 3+3+3+3+4.
    const sec = pattern.cell === "8n" ? 60 / BPM / 2 : 60 / BPM / 4;
    let t = 0;
    const events: SequenceEvent[] = [];
    let cellIdx = 0;
    for (const groupSize of pattern.groups) {
      for (let i = 0; i < groupSize; i++) {
        const isAccent = i === 0;
        const semis = isAccent ? 0 : scaleSemis[cellIdx % 7];
        events.push({
          time: t,
          notes: [midiToPitchName(tonicMidi + semis, k.useFlats)],
          duration: pattern.cell,
          velocity: isAccent ? 0.9 : 0.55,
        });
        t += sec;
        cellIdx += 1;
      }
    }
    // Pad with a tonic resolution at the end
    events.push({
      time: t,
      notes: [midiToPitchName(tonicMidi, k.useFlats)],
      duration: pattern.cell,
    });
    await playSequence(events, BPM);
  };

  // Master keyboard: the seven scale notes
  const masterHighlights: KeyHighlight[] = scaleSemis.map((s, i) => ({
    pc: ((k.tonicPc + s) % 12 + 12) % 12,
    tone: i === 0 ? "root" : i === 4 ? "voice" : "highlight",
    label: ["1", "2", "♭3", "4", "5", "♭6", k.quality === "harmonic-minor" ? "7" : "♭7"][i],
  }));

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The seven minor-scale degrees"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Switch between <strong>Natural Minor</strong> (♭7) and{" "}
          <strong>Harmonic Minor</strong> (raised 7) to feel how the
          7th degree changes everything about how the scale resolves.
          Jason's chart also lists the Dorian (natural 6th) and melodic
          minor (natural 6th and 7th) — every one of them has the ♭3.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="Where we are" title="Pivot thirds, but now in the minor — and louder">
          <p>
            Lesson 19's drill — pinky/thumb on the anchor, third pairs
            floating around it — moves to <strong>minor</strong> scales
            here. Then on top of the same finger drill, we layer rhythmic
            complications: <strong>accent groupings</strong> inside 4/4 (the
            3-3-2 pattern, the 3-3-3-3-4 pattern), and finally{" "}
            <strong>odd time signatures</strong> (5/8, 7/8) — each broken
            into small chunks the body can feel.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Seven is now not thought of as seven, it's thought of as three
            meets two meets two which are tiny numbers."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Step 1" title="Get the minor pivot drill clean first">
          <p>
            Before you put accents anywhere, do the basic pivot drill in
            <strong> {k.tonicName} {k.quality === "harmonic-minor" ? "harmonic minor" : "natural minor"}</strong>:
            pinky on the tonic (high), float third pairs underneath,
            triplets at moderate tempo. Then switch the pinky to the fifth.
            Then the thumb — both with tonic and fifth as the pivot.
          </p>
          <p>
            The minor third on degree 3 (♭3) is what the ear locks onto.
            Every third pair you play has to <em>fit</em> against the new
            mood; if a pair clashes, drop it for now.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The accent grid" title="Pick a grouping, see the bar light up">
          <p>
            Here's where the lesson becomes a rhythmic playground. Each
            grouping below shows you how a bar's worth of equal-duration
            beats can be re-felt as <strong>chunks of 3, 2 (or 4)</strong>.
            The chunk-starts get accents; the rest fills around them.
          </p>
          <div className="my-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            {PATTERNS.map((p, i) => {
              const active = i === patternIdx;
              return (
                <motion.button
                  key={p.id}
                  layout
                  onClick={() => setPatternIdx(i)}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                  }`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className={`text-[14px] font-bold ${active ? "text-amber-200" : "text-cyan-100"}`}>
                      {p.label}
                    </div>
                    <span className="rounded-md bg-zinc-800 px-2 py-0.5 font-mono text-[10px] text-cyan-300">
                      {p.meter}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">
                    {p.description}
                  </div>
                </motion.button>
              );
            })}
          </div>
          <AccentGrid groups={pattern.groups} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playPattern}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Hear "{pattern.label}" in {k.tonicName} {k.quality === "harmonic-minor" ? "harmonic minor" : "natural minor"}
            </button>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The body trick" title="Vocalise the count first">
          <p>
            Don't go straight to the keyboard. <strong>Say the count out
            loud</strong> first. For 7/8 grouped 3+2+2, that's "ONE-two-three
            ONE-two ONE-two — ONE-two-three ONE-two ONE-two…". Once your
            body is comfortable with the verbal pulse, your fingers will
            follow.
          </p>
          <PIInsight>
            <p>
              An odd-meter pattern is rarely "hard" because of math. It
              feels hard when you try to count every beat with equal
              weight. Once you accent only the chunk-starts, the brain
              relaxes — the body just feels three chunks of 3+2+2.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Konnakol shortcut" title="Speak the rhythm in syllables">
          <p>
            South Indian rhythmic vocabulary (Konnakol) gives you a word
            for each group size: TA-KA for 2, TA-KI-TA for 3,
            TA-KA-DI-MI for 4. Speaking the pattern as syllables before
            playing puts it in your body. Try it: "TA-KI-TA TA-KI-TA TA-KA"
            for the 3-3-2 pattern, slowly, for ten seconds, before reaching
            for the keys.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The minor pivot loop with the active accent grid">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="20"
            label={`Pivot thirds · ${k.tonicName} ${k.quality === "harmonic-minor" ? "harmonic minor" : "natural minor"}`}
            originalBpm={88}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Build it from the bottom up. Pivots clean first; then
                accents in 4/4; then odd meters. Each rung adds one
                rhythmic dimension.
              </>
            }
            rungs={[
              { title: "Minor pivot, plain triplets", minutes: 4, accent: "amber",   detail: "Pinky on tonic. Triplets. No accents yet. Lock the basic drill in natural minor first, then harmonic." },
              { title: "3-3-2 inside 4/4",             minutes: 4, accent: "cyan",    detail: "Same notes, but now the accents fall on beats 1, 4 and 7 of each 8 eighths. Feel the 3+3+2 chunks." },
              { title: "5/8 (3+2)",                     minutes: 4, accent: "rose",    detail: "Drop a beat. Group as 3+2. Vocalise it before you play. Mission: Impossible territory." },
              { title: "7/8 (3+2+2)",                   minutes: 4, accent: "violet",  detail: "Add another chunk. 3+2+2 = 7. Progressive-rock playground. Use the Konnakol syllable trick if your fingers stall." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: AccentGrid — sixteen-cell strip with chunk groupings ── */
function AccentGrid({ groups }: { groups: number[] }) {
  const totalCells = groups.reduce((a, b) => a + b, 0);
  // colour palette per chunk
  const palette = ["bg-amber-400", "bg-cyan-400", "bg-rose-400", "bg-emerald-400", "bg-violet-400"];

  let cellsRendered = 0;
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ▥ Accent grid · {totalCells} cells / {groups.join(" + ")}
        </div>
      </div>
      <div className="flex gap-1">
        {groups.map((size, gi) => {
          const cells = Array.from({ length: size }, (_, ci) => {
            const cellIdx = cellsRendered + ci;
            const isAccent = ci === 0;
            return (
              <motion.div
                key={`${gi}-${ci}`}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: cellIdx * 0.03, duration: 0.25 }}
                className={`flex h-10 flex-1 items-end justify-center rounded ${
                  isAccent
                    ? `${palette[gi % palette.length]} text-zinc-900`
                    : "bg-zinc-800 text-cyan-300/70"
                } pb-1 font-mono text-[10px] font-bold`}
              >
                {isAccent ? "•" : "·"}
              </motion.div>
            );
          });
          cellsRendered += size;
          return (
            <div key={gi} className="flex flex-1 gap-0.5 rounded-md ring-1 ring-zinc-700 p-0.5">
              {cells}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-1 text-[11px] uppercase tracking-wider text-cyan-300/70">
        {groups.map((size, i) => (
          <div key={i} className="flex-1 text-center" style={{ flexGrow: size }}>
            chunk {i + 1} · {size}
          </div>
        ))}
      </div>
      <p className="mt-2 text-[12px] italic text-cyan-300/70">
        Coloured cell = accent (chunk start). Grey cell = filler. Read
        chunk-by-chunk: count the small numbers, not the bar.
      </p>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
